import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './InpatientManage.css';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import {
  FaBed,
  FaBuilding,
  FaPlus,
  FaEdit,
  FaTrashAlt,
  FaStethoscope,
  FaCheckCircle,
  FaExchangeAlt,
  FaFileInvoiceDollar,
  FaLayerGroup,
  FaProcedures
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import AddFloorModal from './AddFloorModal';
import AddRoomModal from './AddRoomModal';
import AddServiceModal from './AddServiceModal';
import HospitalFloorsModal from './HospitalFloorsModal';
import RoomTypesModal from './RoomTypesModal';
import BedTypesModal from './BedTypesModal';
import ChargeConfigurationModal from './ChargeConfigurationModal';

const InpatientManage = ({ defaultTab }) => {
  const { subTab } = useParams();

  const [activeTab, setActiveTab] = useState(() => {
    if (subTab?.toLowerCase() === 'services') return 'services';
    if (subTab?.toLowerCase() === 'rooms') return 'rooms';
    return defaultTab || 'rooms';
  });
  const [roomsSubTab, setRoomsSubTab] = useState('rooms'); // 'rooms' | 'charges'

  useEffect(() => {
    if (subTab?.toLowerCase() === 'services') {
      setActiveTab('services');
    } else if (subTab?.toLowerCase() === 'rooms') {
      setActiveTab('rooms');
    } else if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [subTab, defaultTab]);

  // Floors & Rooms State
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);

  /**
   * Normalise a backend HospitalRoom into the flat shape the UI expects.
   * Backend returns { id, floor: { id, floorName }, roomType, roomName, ... }
   * UI was built against { id, floor (string), room (string), ... }
   */
  const normaliseRoom = (r) => ({
    id: r.id,
    floor: r.floor?.floorName ?? '',
    floorId: r.floor?.id,
    roomType: r.roomType,
    room: r.roomName,
    totalBeds: r.totalBeds,
    occupiedBeds: r.occupiedBeds ?? 0,
    bedType: r.bedType,
    status: r.status,
  });

  /**
   * Normalise backend charge data into the grouped { roomType, rules, items[] } shape
   * that the UI renders in the Charges tab.
   * Backend returns:
   *   configs: [{ id, roomType, frequency, fullDayThresholdHours, halfDayEnabled, halfDayThresholdHours }]
   *   items:   [{ id, roomType, chargeType, unitCharge, status }]
   * We merge them into the roomTypeCharges grouped array.
   */
  const buildChargeGroups = (configs, items) => {
    // Build a lookup: roomType -> rules object
    const configMap = {};
    configs.forEach((c) => {
      configMap[c.roomType] = {
        frequency: c.frequency,
        fullDayThreshold: `${c.fullDayThresholdHours} hours`,
        halfDayEnabled: c.halfDayEnabled,
        halfDayThreshold: `${c.halfDayThresholdHours} hours`,
      };
    });

    // Group items by roomType
    const grouped = {};
    items.forEach((item) => {
      if (!grouped[item.roomType]) {
        grouped[item.roomType] = {
          roomType: item.roomType,
          rules: configMap[item.roomType] ?? {
            frequency: 'day',
            fullDayThreshold: '12 hours',
            halfDayEnabled: true,
            halfDayThreshold: '3 hours',
          },
          items: [],
        };
      }
      grouped[item.roomType].items.push({
        id: item.id,
        chargeType: item.chargeType,
        unitCharge: item.unitCharge,
        status: item.status,
      });
    });

    // Also add room types that have a config but no items yet
    configs.forEach((c) => {
      if (!grouped[c.roomType]) {
        grouped[c.roomType] = {
          roomType: c.roomType,
          rules: configMap[c.roomType],
          items: [],
        };
      }
    });

    return Object.values(grouped);
  };

  /** Load floors from backend. Called on mount and after add/delete floor. */
  const fetchFloors = async () => {
    try {
      const res = await axios.get(ENDPOINTS.GET_ALL_FLOORS);
      // Backend returns [{ id, floorName, sortOrder, active }]
      // Store raw objects so AddRoomModal can resolve floorId from name
      setFloors(res.data);
    } catch (err) {
      console.error('Failed to load floors:', err);
      toast.error('Could not load floors from server.');
    }
  };

  /** Load all rooms from backend. Called on mount and after add/edit/delete room. */
  const fetchRooms = async () => {
    try {
      const res = await axios.get(ENDPOINTS.GET_ALL_ROOMS);
      setRooms(res.data.map(normaliseRoom));
    } catch (err) {
      console.error('Failed to load rooms:', err);
      toast.error('Could not load rooms from server.');
    }
  };

  /**
   * Load charge configs + charge items from backend and merge into grouped state.
   * Called on mount and after any charge item or config update.
   */
  const fetchCharges = async () => {
    try {
      const [configsRes, itemsRes] = await Promise.all([
        axios.get(ENDPOINTS.GET_ALL_CHARGE_CONFIGS),
        axios.get(ENDPOINTS.GET_ALL_CHARGE_ITEMS),
      ]);
      setRoomTypeCharges(buildChargeGroups(configsRes.data, itemsRes.data));
    } catch (err) {
      console.error('Failed to load room charges:', err);
      toast.error('Could not load room charges from server.');
    }
  };

  const [roomTypes, setRoomTypes] = useState([]);
  const [bedTypes, setBedTypes] = useState([]);

  /** Load room types from backend (mapped to room_types table). */
  const fetchRoomTypes = async () => {
    try {
      const res = await axios.get(ENDPOINTS.GET_ALL_ROOM_TYPES);
      setRoomTypes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load room types:', err);
    }
  };

  /** Load bed types from backend (mapped to bed_types table). */
  const fetchBedTypes = async () => {
    try {
      const res = await axios.get(ENDPOINTS.GET_ALL_BED_TYPES);
      setBedTypes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load bed types:', err);
    }
  };

  /** Load inpatient services from backend (mapped to inpatient_services table). */
  const fetchServices = async () => {
    try {
      const res = await axios.get(ENDPOINTS.GET_ALL_INPATIENT_SERVICES);
      setServices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load inpatient services:', err);
    }
  };

  /** On mount: fetch floors, rooms, room types, bed types, charges, and services from backend. */
  useEffect(() => {
    fetchFloors();
    fetchRooms();
    fetchCharges();
    fetchRoomTypes();
    fetchBedTypes();
    fetchServices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search & Filter State (Rooms)
  const [roomSearch, setRoomSearch] = useState('');
  const [selectedFloorFilter, setSelectedFloorFilter] = useState('ALL');
  const [selectedRoomTypeFilter, setSelectedRoomTypeFilter] = useState('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('ALL');

  // Room Charges State
  const [roomTypeCharges, setRoomTypeCharges] = useState([]);
  const [chargeSearch, setChargeSearch] = useState('');
  const [showChargeConfigModal, setShowChargeConfigModal] = useState(false);
  const [selectedConfigRoomType, setSelectedConfigRoomType] = useState(null);

  // Inline Add/Edit State for Room Charges
  const [showInlineAdd, setShowInlineAdd] = useState(false);
  const [inlineRoomType, setInlineRoomType] = useState('General Ward');
  const [inlineChargeType, setInlineChargeType] = useState('BED CHARGES');
  const [inlineUnitCharge, setInlineUnitCharge] = useState('');
  const [inlineStatus, setInlineStatus] = useState('Active');

  const [editingItemId, setEditingItemId] = useState(null);
  const [editChargeType, setEditChargeType] = useState('');
  const [editUnitCharge, setEditUnitCharge] = useState('');
  const [editStatus, setEditStatus] = useState('Active');


  /**
   * Persist updated billing rules for a room type to the backend.
   * Called from the ChargeConfigurationModal when staff saves threshold settings.
   *
   * @param {string} targetRoomType - The room type whose rules are being updated.
   * @param {object} newRules - { frequency, fullDayThreshold (string), halfDayEnabled, halfDayThreshold (string) }
   */
  const handleSaveConfigRules = async (targetRoomType, newRules) => {
    try {
      // Convert UI string thresholds (e.g. "12 hours") to integer hours for backend
      const parseHours = (str) => parseInt((str || '').replace(' hours', '').trim(), 10) || 12;

      await axios.post(ENDPOINTS.UPSERT_CHARGE_CONFIG, {
        roomType: targetRoomType,
        frequency: newRules.frequency,
        fullDayThresholdHours: parseHours(newRules.fullDayThreshold),
        halfDayEnabled: newRules.halfDayEnabled,
        halfDayThresholdHours: parseHours(newRules.halfDayThreshold),
      });

      toast.success(`Billing rules saved for "${targetRoomType}"`);
      fetchCharges(); // refresh grouped charge state from backend
    } catch (err) {
      console.error('Failed to save config rules:', err);
      toast.error('Could not save billing rules. Please try again.');
    }
  };

  /**
   * Save a charge line item to the backend.
   * If itemPayload.id is a backend number ID → update (PUT).
   * If itemPayload.id is missing or a Date.now() temp id → create (POST).
   *
   * This is called internally by handleSaveInlineAdd and handleSaveInlineEdit.
   */
  const handleSaveCharge = async (itemPayload) => {
    try {
      const isNew = !itemPayload.id || (typeof itemPayload.id === 'number' && itemPayload.id > 1_000_000_000);
      if (isNew) {
        await axios.post(ENDPOINTS.ADD_CHARGE_ITEM, {
          roomType: itemPayload.roomType,
          chargeType: itemPayload.chargeType,
          unitCharge: itemPayload.unitCharge,
          status: itemPayload.status,
        });
      } else {
        await axios.put(ENDPOINTS.UPDATE_CHARGE_ITEM(itemPayload.id), {
          chargeType: itemPayload.chargeType,
          unitCharge: itemPayload.unitCharge,
          status: itemPayload.status,
        });
      }
      await fetchCharges(); // refresh state from backend after save
    } catch (err) {
      console.error('Failed to save charge item:', err);
      toast.error('Could not save charge. Please try again.');
      throw err; // re-throw so callers can catch
    }
  };


  /**
   * Handle the inline "Add Room Charge" form submission.
   * Validates, then calls handleSaveCharge (which POSTs to backend).
   */
  const handleSaveInlineAdd = async () => {
    if (!inlineChargeType.trim()) {
      toast.error("Please enter a charge type");
      return;
    }
    if (!inlineUnitCharge || isNaN(inlineUnitCharge) || Number(inlineUnitCharge) < 0) {
      toast.error("Please enter a valid unit charge amount");
      return;
    }

    const payload = {
      id: null, // null = new item → POST
      roomType: inlineRoomType,
      chargeType: inlineChargeType.toUpperCase().trim(),
      unitCharge: Number(inlineUnitCharge),
      status: inlineStatus
    };

    try {
      await handleSaveCharge(payload);
      toast.success(`Added "${payload.chargeType}" to ${inlineRoomType}`);
      setInlineChargeType('');
      setInlineUnitCharge('');
      setShowInlineAdd(false);
    } catch (_) {
      // Error already reported by handleSaveCharge
    }
  };


  /**
   * Handle the inline Edit row save for a charge item.
   * Validates, then calls handleSaveCharge (which PUTs to backend).
   */
  const handleSaveInlineEdit = async (roomType, itemId) => {
    if (!editChargeType.trim()) {
      toast.error("Please enter a charge type");
      return;
    }
    if (!editUnitCharge || isNaN(editUnitCharge) || Number(editUnitCharge) < 0) {
      toast.error("Please enter a valid unit charge amount");
      return;
    }

    const payload = {
      id: itemId,
      roomType,
      chargeType: editChargeType.toUpperCase().trim(),
      unitCharge: Number(editUnitCharge),
      status: editStatus
    };

    try {
      await handleSaveCharge(payload);
      toast.success(`Updated "${payload.chargeType}" for ${roomType}`);
      setEditingItemId(null);
    } catch (_) {
      // Error already reported by handleSaveCharge
    }
  };


  /** Delete a single charge line item via backend, then refresh. */
  const handleDeleteCharge = async (chargeId, roomType) => {
    if (window.confirm(`Are you sure you want to delete this charge item for "${roomType}"?`)) {
      try {
        await axios.delete(ENDPOINTS.DELETE_CHARGE_ITEM(chargeId));
        toast.success(`Charge item deleted.`);
        await fetchCharges();
      } catch (err) {
        console.error('Failed to delete charge item:', err);
        toast.error('Could not delete charge item. Please try again.');
      }
    }
  };

  /** Toggle a charge item status via backend, then refresh. */
  const handleToggleChargeStatus = async (chargeId) => {
    try {
      await axios.put(ENDPOINTS.TOGGLE_CHARGE_ITEM_STATUS(chargeId));
      await fetchCharges();
    } catch (err) {
      console.error('Failed to toggle charge status:', err);
      toast.error('Could not toggle charge status. Please try again.');
    }
  };


  // Services State (Mapped to inpatient_services table)
  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedServiceRoomTypeFilter, setSelectedServiceRoomTypeFilter] = useState('ALL');
  const [selectedServiceStatusFilter, setSelectedServiceStatusFilter] = useState('ALL');
  const [selectedServiceEditableFilter, setSelectedServiceEditableFilter] = useState('ALL');

  // Inline Add / Edit State for Inpatient Services
  const [showInlineServiceAdd, setShowInlineServiceAdd] = useState(false);
  const [inlineServiceRoomType, setInlineServiceRoomType] = useState('ALL');
  const [inlineServiceType, setInlineServiceType] = useState('');
  const [inlineServiceCode, setInlineServiceCode] = useState('');
  const [inlineServiceUnitCharge, setInlineServiceUnitCharge] = useState('');
  const [inlineServiceStatus, setInlineServiceStatus] = useState('Active');
  const [inlineServiceChargeEditable, setInlineServiceChargeEditable] = useState(true);

  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editServiceRoomType, setEditServiceRoomType] = useState('ALL');
  const [editServiceType, setEditServiceType] = useState('');
  const [editServiceCode, setEditServiceCode] = useState('');
  const [editServiceUnitCharge, setEditServiceUnitCharge] = useState('');
  const [editServiceStatus, setEditServiceStatus] = useState('Active');
  const [editServiceChargeEditable, setEditServiceChargeEditable] = useState(true);

  /** Save a service via modal (POST or PUT). */
  const handleSaveService = async (payload) => {
    try {
      if (payload.id) {
        await axios.put(ENDPOINTS.UPDATE_INPATIENT_SERVICE(payload.id), payload);
        toast.success(`Service "${payload.serviceType || payload.name}" updated successfully.`);
      } else {
        await axios.post(ENDPOINTS.ADD_INPATIENT_SERVICE, payload);
        toast.success(`Service "${payload.serviceType || payload.name}" added successfully.`);
      }
      await fetchServices();
    } catch (err) {
      console.error('Failed to save service:', err);
      toast.error('Could not save service. Please try again.');
    }
  };

  /** Save an inline new service. */
  const handleSaveInlineServiceAdd = async () => {
    if (!inlineServiceType.trim()) {
      toast.error('Please enter a service type/name.');
      return;
    }
    if (!inlineServiceUnitCharge || isNaN(inlineServiceUnitCharge) || Number(inlineServiceUnitCharge) < 0) {
      toast.error('Please enter a valid unit charge.');
      return;
    }

    try {
      await axios.post(ENDPOINTS.ADD_INPATIENT_SERVICE, {
        roomType: inlineServiceRoomType || 'ALL',
        serviceType: inlineServiceType.trim(),
        serviceCode: inlineServiceCode.trim() || `SRV-${Math.floor(1000 + Math.random() * 9000)}`,
        unitCharge: Number(inlineServiceUnitCharge),
        status: inlineServiceStatus,
        chargeEditable: inlineServiceChargeEditable
      });
      toast.success(`Service "${inlineServiceType}" added successfully.`);
      setInlineServiceType('');
      setInlineServiceCode('');
      setInlineServiceUnitCharge('');
      setInlineServiceStatus('Active');
      setInlineServiceChargeEditable(true);
      setShowInlineServiceAdd(false);
      await fetchServices();
    } catch (err) {
      console.error('Failed to add service:', err);
      toast.error('Could not add service. Please try again.');
    }
  };

  /** Save an inline edited service. */
  const handleSaveInlineServiceEdit = async (serviceId) => {
    if (!editServiceType.trim()) {
      toast.error('Please enter a service type/name.');
      return;
    }
    if (!editServiceUnitCharge || isNaN(editServiceUnitCharge) || Number(editServiceUnitCharge) < 0) {
      toast.error('Please enter a valid unit charge.');
      return;
    }

    try {
      await axios.put(ENDPOINTS.UPDATE_INPATIENT_SERVICE(serviceId), {
        roomType: editServiceRoomType || 'ALL',
        serviceType: editServiceType.trim(),
        serviceCode: editServiceCode.trim(),
        unitCharge: Number(editServiceUnitCharge),
        status: editServiceStatus,
        chargeEditable: editServiceChargeEditable
      });
      toast.success(`Service "${editServiceType}" updated.`);
      setEditingServiceId(null);
      await fetchServices();
    } catch (err) {
      console.error('Failed to update service:', err);
      toast.error('Could not update service. Please try again.');
    }
  };

  /** Toggle a service status (Active / Inactive) via backend. */
  const handleToggleServiceStatus = async (serviceId) => {
    try {
      const res = await axios.patch(ENDPOINTS.TOGGLE_INPATIENT_SERVICE_STATUS(serviceId));
      toast.info(`Service status changed to ${res.data.status}`);
      await fetchServices();
    } catch (err) {
      console.error('Failed to toggle service status:', err);
      toast.error('Could not toggle service status.');
    }
  };

  /** Delete a service via backend. */
  const handleDeleteService = async (serviceId, serviceName) => {
    if (window.confirm(`Are you sure you want to delete service "${serviceName}"?`)) {
      try {
        await axios.delete(ENDPOINTS.DELETE_INPATIENT_SERVICE(serviceId));
        toast.success(`Service "${serviceName}" deleted.`);
        await fetchServices();
      } catch (err) {
        console.error('Failed to delete service:', err);
        toast.error('Could not delete service.');
      }
    }
  };

  // Modals state
  const [showHospitalFloorsModal, setShowHospitalFloorsModal] = useState(false);
  const [showRoomTypesModal, setShowRoomTypesModal] = useState(false);
  const [showBedTypesModal, setShowBedTypesModal] = useState(false);
  const [showAddFloorModal, setShowAddFloorModal] = useState(false);
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [roomToEdit, setRoomToEdit] = useState(null);

  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState(null);

  // Add/Delete Floor — backend-backed
  /**
   * Called by AddFloorModal after staff enters a floor name.
   * POSTs to backend, then refreshes the floors list.
   */
  const handleSaveFloor = async (newFloor) => {
    try {
      await axios.post(ENDPOINTS.ADD_FLOOR, { floorName: newFloor.name });
      toast.success(`Floor "${newFloor.name}" added successfully.`);
      await fetchFloors();
    } catch (err) {
      const msg = err.response?.data || 'Could not add floor. Please try again.';
      toast.error(msg);
    }
  };

  /**
   * Called from HospitalFloorsModal's inline add field.
   * Same as handleSaveFloor but accepts a plain string.
   */
  const handleAddFloorDirect = async (floorName) => {
    try {
      await axios.post(ENDPOINTS.ADD_FLOOR, { floorName });
      toast.success(`Floor "${floorName}" added.`);
      await fetchFloors();
    } catch (err) {
      const msg = err.response?.data || 'Could not add floor.';
      toast.error(msg);
    }
  };


  /**
   * Delete a floor by name.
   * Uses the in-memory floors state to resolve ID (no extra network call).
   * Backend will reject if rooms still exist on this floor.
   */
  const handleDeleteFloor = async (floorName) => {
    const match = floors.find((f) => f.floorName === floorName);
    if (!match) {
      toast.error(`Floor "${floorName}" not found.`);
      return;
    }
    try {
      await axios.delete(ENDPOINTS.DELETE_FLOOR(match.id));
      toast.success(`Floor "${floorName}" deleted.`);
      await fetchFloors();
      await fetchRooms();
    } catch (err) {
      const msg = err.response?.data || 'Could not delete floor. Please try again.';
      toast.error(msg);
    }
  };

  /**
   * Save a room (add or update) via backend.
   * Called by AddRoomModal. The modal passes { floorId, roomType, roomName, ... }.
   * After save we refresh the rooms list from the backend.
   */
  const handleSaveRoom = async (savedRoom) => {
    try {
      if (roomToEdit) {
        // UPDATE: savedRoom must contain the room ID and updated fields
        await axios.put(ENDPOINTS.UPDATE_ROOM(savedRoom.id), {
          floorId: savedRoom.floorId,
          roomType: savedRoom.roomType,
          roomName: savedRoom.room ?? savedRoom.roomName,
          totalBeds: savedRoom.totalBeds,
          occupiedBeds: savedRoom.occupiedBeds ?? 0,
          bedType: savedRoom.bedType,
          status: savedRoom.status,
        });
        toast.success(`Room "${savedRoom.room ?? savedRoom.roomName}" updated.`);
      } else {
        // CREATE: POST new room
        await axios.post(ENDPOINTS.ADD_ROOM, {
          floorId: savedRoom.floorId,
          roomType: savedRoom.roomType,
          roomName: savedRoom.room ?? savedRoom.roomName,
          totalBeds: savedRoom.totalBeds,
          occupiedBeds: savedRoom.occupiedBeds ?? 0,
          bedType: savedRoom.bedType,
          status: savedRoom.status ?? 'Available',
        });
        toast.success(`Room "${savedRoom.room ?? savedRoom.roomName}" added.`);
      }
      await fetchRooms(); // refresh list from backend
    } catch (err) {
      const msg = err.response?.data || 'Could not save room. Please try again.';
      toast.error(msg);
    }
  };

  /**
   * Delete a room by ID via backend.
   * Backend rejects deletion if the room has occupied beds.
   */
  const handleDeleteRoom = async (roomId, roomNumber) => {
    if (window.confirm(`Are you sure you want to delete "${roomNumber}"?`)) {
      try {
        await axios.delete(ENDPOINTS.DELETE_ROOM(roomId));
        toast.success(`Hospital Room "${roomNumber}" deleted successfully.`);
        await fetchRooms();
      } catch (err) {
        const msg = err.response?.data || `Cannot delete "${roomNumber}". It may have occupied beds.`;
        toast.error(msg);
      }
    }
  };

  /**
   * Toggle a room's status between Available ↔ Under Maintenance via backend.
   * Backend rejects if there are occupied beds.
   */
  const handleToggleRoomStatus = async (roomId) => {
    try {
      await axios.put(ENDPOINTS.TOGGLE_ROOM_STATUS(roomId));
      await fetchRooms();
    } catch (err) {
      const msg = err.response?.data || 'Could not toggle room status.';
      toast.error(msg);
    }
  };


  // Filtered Rooms List
  const filteredRooms = rooms.filter((r) => {
    const matchesSearch =
      r.room.toLowerCase().includes(roomSearch.toLowerCase()) ||
      r.floor.toLowerCase().includes(roomSearch.toLowerCase()) ||
      r.roomType.toLowerCase().includes(roomSearch.toLowerCase()) ||
      r.bedType.toLowerCase().includes(roomSearch.toLowerCase());

    const matchesFloor = selectedFloorFilter === 'ALL' || r.floor === selectedFloorFilter;
    const matchesRoomType = selectedRoomTypeFilter === 'ALL' || r.roomType === selectedRoomTypeFilter;
    const matchesStatus = selectedStatusFilter === 'ALL' || r.status === selectedStatusFilter;

    return matchesSearch && matchesFloor && matchesRoomType && matchesStatus;
  });



  // Filtered Services List (Matching table columns: Room Type, Service Type, Unit Charge, Status, Charge Editable)
  const filteredServices = services.filter((s) => {
    const sRoomType = s.roomType || 'ALL';
    const sType = s.serviceType || s.name || '';
    const sCode = s.serviceCode || s.code || '';
    const sStatus = s.status || 'Active';
    const sEditable = s.chargeEditable !== false;

    // Search query
    const q = serviceSearch.trim().toLowerCase();
    const matchesSearch =
      !q ||
      sRoomType.toLowerCase().includes(q) ||
      sType.toLowerCase().includes(q) ||
      sCode.toLowerCase().includes(q) ||
      String(s.unitCharge || s.price || '').includes(q);

    // Filters
    const matchesRoomType =
      selectedServiceRoomTypeFilter === 'ALL' ||
      sRoomType.toLowerCase() === selectedServiceRoomTypeFilter.toLowerCase();

    const matchesStatus =
      selectedServiceStatusFilter === 'ALL' ||
      sStatus.toLowerCase() === selectedServiceStatusFilter.toLowerCase();

    const matchesEditable =
      selectedServiceEditableFilter === 'ALL' ||
      (selectedServiceEditableFilter === 'YES' && sEditable) ||
      (selectedServiceEditableFilter === 'NO' && !sEditable);

    return matchesSearch && matchesRoomType && matchesStatus && matchesEditable;
  });

  // Inactive lookup sets
  const inactiveRoomTypesSet = new Set(
    (roomTypes || []).filter((rt) => rt.status === 'Inactive').map((rt) => (rt.name || '').toLowerCase())
  );
  const inactiveBedTypesSet = new Set(
    (bedTypes || []).filter((bt) => bt.status === 'Inactive').map((bt) => (bt.name || '').toLowerCase())
  );

  // Calculations for Rooms tab
  const totalRoomsCount = rooms.length;
  const totalBedsCount = rooms.reduce((acc, r) => acc + Number(r.totalBeds || 1), 0);
  const roomTypesCount = roomTypes && roomTypes.length > 0 ? roomTypes.length : Array.from(new Set(rooms.map((r) => r.roomType).filter(Boolean))).length;
  const occupiedBedsCount = rooms.reduce((acc, r) => acc + (r.status === 'Occupied' ? Number(r.totalBeds || 1) : Number(r.occupiedBeds || 0)), 0);
  const maintenanceBedsCount = rooms.reduce((acc, r) => {
    const isRoomInactive =
      r.status === 'Under Maintenance' ||
      r.status === 'Inactive' ||
      r.active === false ||
      inactiveRoomTypesSet.has((r.roomType || '').toLowerCase()) ||
      inactiveBedTypesSet.has((r.bedType || '').toLowerCase());
    return isRoomInactive ? acc + Number(r.totalBeds || 1) : acc;
  }, 0);
  const availableBedsCount = Math.max(0, totalBedsCount - occupiedBedsCount - maintenanceBedsCount);

  return (
    <div className="ip-manage-container">
      <div className="ip-manage-master-card">
        {/* Top Accent Gradient Bar */}
        <div className="ip-manage-accent-bar" />

        {/* Master Toolbar Header */}
        <div className="ip-manage-toolbar">
          <div className="ip-manage-title-group">
            {activeTab === 'rooms' ? (
              <>
                <h2>
                  <FaBed color="#2563eb" /> Hospital Rooms & Tariff Management
                </h2>
                <p className="ip-manage-subtitle">
                  Manage hospital floors, room allocations, bed types, and room tariff charges.
                </p>
              </>
            ) : (
              <>
                <h2>
                  <FaStethoscope color="#2563eb" /> Inpatient Services & Billing Charges
                </h2>
                <p className="ip-manage-subtitle">
                  Manage inpatient service catalog, critical care charges, and daily nursing rates.
                </p>
              </>
            )}
          </div>

          {/* Sub-Tabs Nav: Rooms Tab vs Room Charges Tab */}
          {activeTab === 'rooms' && (
            <div className="ip-manage-tabs-nav">
              <button
                className={`ip-manage-tab-btn ${roomsSubTab === 'rooms' ? 'active-tab' : ''}`}
                onClick={() => setRoomsSubTab('rooms')}
              >
                <FaBed size={15} /> Rooms Tab
              </button>
              <button
                className={`ip-manage-tab-btn ${roomsSubTab === 'charges' ? 'active-tab' : ''}`}
                onClick={() => setRoomsSubTab('charges')}
              >
                <FaFileInvoiceDollar size={15} /> Room Charges Tab
              </button>
            </div>
          )}
        </div>

        {/* === ROOMS VIEW: ROOMS TAB CONTENT === */}
        {activeTab === 'rooms' && roomsSubTab === 'rooms' && (
          <>
            {/* Overview Summary Cards (Hospital Floors & Hospital Rooms) */}
            {/* Overview Summary Cards (Hospital Floors, Hospital Rooms & Bed Status) */}
            <div className="ip-overview-cards-row">
              {/* Card 1: Hospital Floors */}
              <div
                className="ip-overview-card"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowHospitalFloorsModal(true)}
                title="Click to view and manage hospital floors"
              >
                <div className="card-info-group">
                  <div className="card-icon-box blue">
                    <FaBuilding size={22} />
                  </div>
                  <div className="card-text">
                    <h4>Hospital Floors</h4>
                    <div className="card-number">{floors.length} Active Floors</div>
                  </div>
                </div>
              </div>

              {/* Card 2: Hospital Rooms */}
              <div
                className="ip-overview-card"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowRoomTypesModal(true)}
                title="Click to view room types breakdown"
              >
                <div className="card-info-group">
                  <div className="card-icon-box emerald">
                    <FaBed size={22} />
                  </div>
                  <div className="card-text">
                    <h4>Hospital Rooms</h4>
                    <div className="card-number" style={{ fontSize: '15px', color: '#0f172a', fontWeight: 700, marginTop: '4px' }}>
                      {totalRoomsCount} Rooms &bull; {totalBedsCount} Beds &bull; {roomTypesCount} Room Types
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Bed Availability Summary */}
              <div className="ip-overview-card">
                <div className="card-info-group">
                  <div className="card-icon-box amber">
                    <FaCheckCircle size={22} />
                  </div>
                  <div className="card-text">
                    <h4>Bed Status</h4>
                    <div className="card-number" style={{ fontSize: '15px', marginTop: '4px' }}>
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>{availableBedsCount} Available</span> &bull;{' '}
                      <span style={{ color: '#dc2626', fontWeight: 700 }}>{occupiedBedsCount} Occupied</span> &bull;{' '}
                      <span style={{ color: '#d97706', fontWeight: 700 }}>{maintenanceBedsCount} Maint</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Control Bar: Search, Filters & Add Room Button (Right Aligned) */}
            <div className="ip-manage-controls-bar">
              <div className="controls-left-group">
                <input
                  type="text"
                  className="manage-search-input"
                  placeholder="Search floor, room number, bed type..."
                  value={roomSearch}
                  onChange={(e) => setRoomSearch(e.target.value)}
                />

                <select
                  className="manage-filter-select"
                  value={selectedFloorFilter}
                  onChange={(e) => setSelectedFloorFilter(e.target.value)}
                >
                  <option value="ALL">All Floors</option>
                  {floors.map((fl) => (
                    <option key={fl.id} value={fl.floorName}>{fl.floorName}</option>
                  ))}
                </select>

                <select
                  className="manage-filter-select"
                  value={selectedRoomTypeFilter}
                  onChange={(e) => setSelectedRoomTypeFilter(e.target.value)}
                >
                  <option value="ALL">All Room Types</option>
                  {Array.from(new Set([
                    ...roomTypes.map((rt) => rt.name),
                    ...rooms.map((r) => r.roomType)
                  ].filter(Boolean))).map((typeName) => {
                    const rtObj = roomTypes.find((rt) => rt.name?.toLowerCase() === typeName.toLowerCase());
                    const isInactive = rtObj && rtObj.status === 'Inactive';
                    return (
                      <option
                        key={typeName}
                        value={typeName}
                        style={{ color: isInactive ? '#dc2626' : 'inherit' }}
                      >
                        {typeName} {isInactive ? '(Inactive)' : ''}
                      </option>
                    );
                  })}
                </select>

                <select
                  className="manage-filter-select"
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="Available">Available</option>
                  <option value="Occupied">Occupied</option>
                  <option value="Under Maintenance">Under Maintenance</option>
                </select>
              </div>

              <div className="controls-right-group" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn-add-floor-secondary"
                  onClick={() => setShowBedTypesModal(true)}
                  style={{
                    padding: '8px 14px',
                    fontSize: '13px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#eff6ff',
                    color: '#2563eb',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  title="Configure and manage bed types"
                >
                  <FaProcedures size={13} /> Bed Types
                </button>
                <button
                  type="button"
                  className="btn-add-floor-primary"
                  onClick={() => {
                    setRoomToEdit(null);
                    setShowAddRoomModal(true);
                  }}
                  style={{ padding: '8px 18px', fontSize: '13px' }}
                >
                  <FaPlus size={11} /> Add Room
                </button>
              </div>
            </div>

            {/* Data Table of Floor, Room Type, Room, Total Beds, Bed Type, Status, Action */}
            <div className="ip-manage-table-responsive">
              <table className="ip-manage-data-table">
                <thead>
                  <tr>
                    <th>Floor</th>
                    <th>Room Type</th>
                    <th>Room</th>
                    <th>Total Beds</th>
                    <th>Bed Type</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRooms.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="ip-manage-empty">
                        No hospital rooms or beds found matching criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredRooms.map((record) => {
                      const statusClass =
                        record.status === 'Available' ? 'available' :
                        record.status === 'Occupied' ? 'occupied' : 'maintenance';

                      return (
                        <tr key={record.id} className="ip-manage-row">
                          <td>
                            <span className="floor-pill">
                              <FaBuilding size={11} color="#64748b" /> {record.floor}
                            </span>
                          </td>
                          <td>
                            <span className="room-type-pill">
                              <FaLayerGroup size={11} color="#2563eb" /> {record.roomType}
                            </span>
                          </td>
                          <td>
                            <span className="room-number-text">{record.room}</span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span className="bed-type-text" style={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <FaBed color={record.status === 'Occupied' ? '#dc2626' : record.occupiedBeds > 0 ? '#d97706' : '#16a34a'} size={13} />
                                {record.totalBeds || 1} {Number(record.totalBeds) > 1 ? 'Beds' : 'Bed'}
                              </span>
                              {record.occupiedBeds > 0 && record.occupiedBeds < (record.totalBeds || 1) && (
                                <span style={{ fontSize: '11px', color: '#d97706', fontWeight: 600 }}>
                                  ({record.occupiedBeds} occupied, {(record.totalBeds || 1) - record.occupiedBeds} avail)
                                </span>
                              )}
                              {(record.status === 'Occupied' || (record.occupiedBeds > 0 && record.occupiedBeds === (record.totalBeds || 1))) && (
                                <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>
                                  (All {record.totalBeds || 1} Beds Occupied)
                                </span>
                              )}
                              {(!record.occupiedBeds || record.occupiedBeds === 0) && record.status === 'Available' && (
                                <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>
                                  (All {record.totalBeds || 1} Beds Available)
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="bed-type-text">{record.bedType}</span>
                          </td>
                          <td>
                            <span className={`status-chip ${statusClass}`}>
                              {record.status}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions-group">
                              <button
                                className="action-btn-icon edit-btn"
                                onClick={() => {
                                  setRoomToEdit(record);
                                  setShowAddRoomModal(true);
                                }}
                                title="Edit Room Details"
                              >
                                <FaEdit size={13} />
                              </button>

                              <button
                                className="action-btn-icon toggle-btn"
                                onClick={() => handleToggleRoomStatus(record.id)}
                                title="Toggle Status (Available / Maintenance)"
                              >
                                <FaExchangeAlt size={11} />
                              </button>

                              <button
                                className="action-btn-icon delete-btn"
                                onClick={() => handleDeleteRoom(record.id, record.room)}
                                title="Delete Room"
                              >
                                <FaTrashAlt size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* === ROOM CHARGES VIEW CONTENT === */}
        {activeTab === 'rooms' && roomsSubTab === 'charges' && (
          <>
            <div className="ip-overview-cards-row">
              <div className="ip-overview-card">
                <div className="card-info-group">
                  <div className="card-icon-box emerald">
                    <FaFileInvoiceDollar size={22} />
                  </div>
                  <div className="card-text">
                    <h4>Room Daily Tariffs & Billing Rules</h4>
                    <div className="card-number">
                      {roomTypeCharges.reduce((acc, g) => acc + g.items.length, 0)} Configured Charges across {roomTypeCharges.length} Room Types
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ip-manage-controls-bar">
              <div className="controls-left-group">
                <input
                  type="text"
                  className="manage-search-input"
                  placeholder="Search room type, charge type..."
                  value={chargeSearch}
                  onChange={(e) => setChargeSearch(e.target.value)}
                />
              </div>
              <div className="controls-right-group">
                <button
                  type="button"
                  className="btn-add-floor-primary"
                  onClick={() => setShowInlineAdd(!showInlineAdd)}
                  style={{ padding: '8px 18px', fontSize: '13px' }}
                >
                  <FaPlus size={11} /> {showInlineAdd ? 'Close Add Form' : 'Add Room Charge'}
                </button>
              </div>
            </div>

            <div className="ip-manage-table-responsive">
              <table className="ip-manage-data-table" style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'center', width: '230px', padding: '14px 10px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      🛏️ ROOM TYPE
                    </th>
                    <th style={{ padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      💵 CHARGE TYPE
                    </th>
                    <th style={{ width: '170px', padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      ₹ UNIT CHARGE
                    </th>
                    <th style={{ width: '120px', padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      ✔ STATUS
                    </th>
                    <th style={{ width: '130px', padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      ⚙️ ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Inline Add Row */}
                  {showInlineAdd && (
                    <tr style={{ background: '#eff6ff', borderBottom: '2px solid #2563eb' }}>
                      <td style={{ padding: '10px 8px', textAlign: 'center', width: '230px' }}>
                        <select
                          value={inlineRoomType}
                          onChange={(e) => setInlineRoomType(e.target.value)}
                          className="manage-filter-select"
                          style={{ width: '100%', fontWeight: '700', padding: '7px 8px', fontSize: '12px' }}
                        >
                          <option value="General Ward">General Ward</option>
                          <option value="ICU">ICU</option>
                          <option value="Semi-Private">Semi-Private</option>
                          <option value="Private Deluxe">Private Deluxe</option>
                          <option value="Emergency Care">Emergency Care</option>
                          <option value="VIP Suite">VIP Suite</option>
                        </select>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <select
                          value={inlineChargeType}
                          onChange={(e) => setInlineChargeType(e.target.value)}
                          className="manage-filter-select"
                          style={{ width: '100%', padding: '7px 10px', fontSize: '13px' }}
                        >
                          <option value="BED CHARGES">BED CHARGES</option>
                          <option value="NURSING FEES">NURSING FEES</option>
                          <option value="DUTY DOCTOR FEES">DUTY DOCTOR FEES</option>
                          <option value="ICU CHARGES">ICU CHARGES</option>
                          <option value="MONITORING FEES">MONITORING FEES</option>
                          <option value="CONSULTATION FEES">CONSULTATION FEES</option>
                          <option value="ROOM CHARGES">ROOM CHARGES</option>
                          <option value="EQUIPMENT CHARGES">EQUIPMENT CHARGES</option>
                          <option value="OXYGEN SUPPORT">OXYGEN SUPPORT</option>
                          <option value="DIET & NUTRITION">DIET & NUTRITION</option>
                          <option value="PATIENT CARE">PATIENT CARE</option>
                        </select>
                      </td>
                      <td style={{ padding: '12px', width: '170px' }}>
                        <input
                          type="number"
                          placeholder="Amount (₹)"
                          value={inlineUnitCharge}
                          onChange={(e) => setInlineUnitCharge(e.target.value)}
                          className="manage-search-input"
                          style={{ minWidth: '100%', padding: '7px 10px', fontSize: '13px' }}
                        />
                      </td>
                      <td style={{ padding: '12px', width: '120px' }}>
                        <select
                          value={inlineStatus}
                          onChange={(e) => setInlineStatus(e.target.value)}
                          className="manage-filter-select"
                          style={{ width: '100%', padding: '7px 8px' }}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </td>
                      <td style={{ padding: '12px', width: '130px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            type="button"
                            onClick={handleSaveInlineAdd}
                            style={{
                              background: '#0284c7',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '7px 10px',
                              fontSize: '12px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            ✓ Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowInlineAdd(false)}
                            style={{
                              background: '#ef4444',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '7px 8px',
                              fontSize: '12px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {roomTypeCharges
                    .filter((group) => {
                      if (!chargeSearch.trim()) return true;
                      const q = chargeSearch.toLowerCase();
                      return (
                        group.roomType.toLowerCase().includes(q) ||
                        group.items.some((i) => i.chargeType.toLowerCase().includes(q))
                      );
                    })
                    .map((group) => {
                      const ruleText = group.rules?.halfDayEnabled !== false
                        ? `Full day charges after ${group.rules?.fullDayThreshold || '12 hours'}. Half day charges between ${group.rules?.halfDayThreshold || '3 hours'} and ${group.rules?.fullDayThreshold || '12 hours'}.`
                        : `Full day charges after ${group.rules?.fullDayThreshold || '12 hours'}. Half day charges disabled.`;

                      return group.items.map((item, index) => (
                        <tr key={`${group.roomType}-${item.id}`} className="ip-manage-row" style={{ borderBottom: '1px solid #e2e8f0' }}>
                          {index === 0 && (
                            <td
                              rowSpan={group.items.length || 1}
                              style={{
                                verticalAlign: 'middle',
                                textAlign: 'center',
                                padding: '10px 8px',
                                borderRight: '1px solid #e2e8f0',
                                background: '#fafafa',
                                width: '230px'
                              }}
                            >
                              <div
                                className="room-type-rule-box"
                                onClick={() => {
                                  setSelectedConfigRoomType(group);
                                  setShowChargeConfigModal(true);
                                }}
                                title="Click to configure threshold hours & half-day charges"
                              >
                                <div className="room-type-header-name">
                                  {group.roomType.toUpperCase()}
                                  <FaEdit size={12} color="#2563eb" />
                                </div>
                                <div className="room-type-rule-desc">
                                  {ruleText}
                                </div>
                                <span className="click-to-edit-hint">Click to edit rules</span>
                              </div>
                            </td>
                          )}

                          {editingItemId === item.id ? (
                            <>
                              <td style={{ padding: '8px 10px' }}>
                                <select
                                  value={editChargeType}
                                  onChange={(e) => setEditChargeType(e.target.value)}
                                  className="manage-filter-select"
                                  style={{ width: '100%', padding: '6px 10px', fontSize: '13px' }}
                                >
                                  <option value="BED CHARGES">BED CHARGES</option>
                                  <option value="NURSING FEES">NURSING FEES</option>
                                  <option value="DUTY DOCTOR FEES">DUTY DOCTOR FEES</option>
                                  <option value="ICU CHARGES">ICU CHARGES</option>
                                  <option value="MONITORING FEES">MONITORING FEES</option>
                                  <option value="CONSULTATION FEES">CONSULTATION FEES</option>
                                  <option value="ROOM CHARGES">ROOM CHARGES</option>
                                  <option value="EQUIPMENT CHARGES">EQUIPMENT CHARGES</option>
                                  <option value="OXYGEN SUPPORT">OXYGEN SUPPORT</option>
                                  <option value="DIET & NUTRITION">DIET & NUTRITION</option>
                                  <option value="PATIENT CARE">PATIENT CARE</option>
                                </select>
                              </td>
                              <td style={{ padding: '8px 10px' }}>
                                <input
                                  type="number"
                                  value={editUnitCharge}
                                  onChange={(e) => setEditUnitCharge(e.target.value)}
                                  className="manage-search-input"
                                  style={{ minWidth: '100%', padding: '6px 10px', fontSize: '13px' }}
                                />
                              </td>
                              <td style={{ padding: '8px 10px' }}>
                                <select
                                  value={editStatus}
                                  onChange={(e) => setEditStatus(e.target.value)}
                                  className="manage-filter-select"
                                  style={{ padding: '6px 8px', fontSize: '13px' }}
                                >
                                  <option value="Active">Active</option>
                                  <option value="Inactive">Inactive</option>
                                </select>
                              </td>
                              <td style={{ padding: '8px 10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveInlineEdit(group.roomType, item.id)}
                                    style={{
                                      background: '#0284c7',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                      fontWeight: '700',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    ✓ Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingItemId(null)}
                                    style={{
                                      background: '#ef4444',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '6px 10px',
                                      fontSize: '12px',
                                      fontWeight: '700',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    ✕ Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td style={{ fontWeight: '700', color: '#334155', letterSpacing: '0.3px' }}>
                                {item.chargeType}
                              </td>
                              <td style={{ fontWeight: '800', color: '#0f172a' }}>
                                ₹ {Number(item.unitCharge).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </td>
                              <td>
                                <span className={`status-chip ${item.status === 'Active' ? 'available' : 'inactive'}`}>
                                  • {item.status.toUpperCase()}
                                </span>
                              </td>
                              <td>
                                <div className="table-actions-group">
                                  <button
                                    className="action-btn-icon edit-btn"
                                    onClick={() => {
                                      setEditingItemId(item.id);
                                      setEditChargeType(item.chargeType);
                                      setEditUnitCharge(item.unitCharge);
                                      setEditStatus(item.status);
                                    }}
                                    title="Edit Charge Item"
                                  >
                                    <FaEdit size={13} />
                                  </button>
                                  <button
                                    className="action-btn-icon toggle-btn"
                                    onClick={() => handleToggleChargeStatus(item.id)}
                                    title="Toggle Active / Inactive"
                                  >
                                    <FaExchangeAlt size={11} />
                                  </button>
                                  <button
                                    className="action-btn-icon delete-btn"
                                    onClick={() => handleDeleteCharge(item.id, group.roomType)}
                                    title="Delete Charge Item"
                                  >
                                    <FaTrashAlt size={12} />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ));
                    })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* === SERVICES VIEW CONTENT (MIGRATED TO DATABASE & ALIGNED WITH ROOM CHARGES) === */}
        {activeTab === 'services' && (
          <>
            <div className="ip-overview-cards-row">
              <div className="ip-overview-card">
                <div className="card-info-group">
                  <div className="card-icon-box purple">
                    <FaStethoscope size={22} />
                  </div>
                  <div className="card-text">
                    <h4>Inpatient Services</h4>
                    <div className="card-number" style={{ fontSize: '15px', color: '#0f172a', fontWeight: 700, marginTop: '4px' }}>
                      {services.length} Total Services &bull;{' '}
                      <span style={{ color: '#16a34a' }}>{services.filter((s) => s.status === 'Active' || !s.status).length} Active</span> &bull;{' '}
                      <span style={{ color: '#dc2626' }}>{services.filter((s) => s.status === 'Inactive').length} Inactive</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ip-manage-controls-bar">
              <div className="controls-left-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="manage-search-input"
                  placeholder="Search room type, service type, code..."
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                />

                <select
                  className="manage-filter-select"
                  value={selectedServiceRoomTypeFilter}
                  onChange={(e) => setSelectedServiceRoomTypeFilter(e.target.value)}
                >
                  <option value="ALL">All Room Types</option>
                  <option value="ALL">🏢 All Rooms (Hospital Wide)</option>
                  {Array.from(new Set([
                    ...roomTypes.map((rt) => rt.name),
                    ...services.map((s) => s.roomType)
                  ].filter((n) => n && n !== 'ALL'))).map((typeName) => {
                    const rtObj = roomTypes.find((rt) => rt.name?.toLowerCase() === typeName.toLowerCase());
                    const isInactive = rtObj && rtObj.status === 'Inactive';
                    return (
                      <option
                        key={typeName}
                        value={typeName}
                        style={{ color: isInactive ? '#dc2626' : 'inherit' }}
                      >
                        {typeName} {isInactive ? '(Inactive)' : ''}
                      </option>
                    );
                  })}
                </select>

                <select
                  className="manage-filter-select"
                  value={selectedServiceStatusFilter}
                  onChange={(e) => setSelectedServiceStatusFilter(e.target.value)}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>

                <select
                  className="manage-filter-select"
                  value={selectedServiceEditableFilter}
                  onChange={(e) => setSelectedServiceEditableFilter(e.target.value)}
                >
                  <option value="ALL">All Charge Types</option>
                  <option value="YES">Editable at Billing (Yes)</option>
                  <option value="NO">Locked Tariff (No)</option>
                </select>
              </div>

              <div className="controls-right-group" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn-add-floor-secondary"
                  onClick={() => setShowInlineServiceAdd(!showInlineServiceAdd)}
                  style={{
                    padding: '8px 14px',
                    fontSize: '13px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#eff6ff',
                    color: '#2563eb',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  title="Quick inline add row"
                >
                  <FaPlus size={11} /> {showInlineServiceAdd ? 'Close Inline Form' : 'Quick Add Row'}
                </button>

                <button
                  type="button"
                  className="btn-add-floor-primary"
                  onClick={() => {
                    setServiceToEdit(null);
                    setShowAddServiceModal(true);
                  }}
                  style={{ padding: '8px 18px', fontSize: '13px' }}
                >
                  <FaPlus size={11} /> Add Service
                </button>
              </div>
            </div>

            <div className="ip-manage-table-responsive">
              <table className="ip-manage-data-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ width: '220px', padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <FaBuilding color="#2563eb" /> Room Type
                      </span>
                    </th>
                    <th style={{ padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <FaStethoscope color="#2563eb" /> Service Type
                      </span>
                    </th>
                    <th style={{ width: '160px', padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      Unit Charge (₹)
                    </th>
                    <th style={{ width: '130px', padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      Status
                    </th>
                    <th style={{ width: '150px', padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>
                      Charge Editable
                    </th>
                    <th style={{ width: '140px', padding: '14px 16px', color: '#1e293b', fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px', textAlign: 'center' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Inline Add Row */}
                  {showInlineServiceAdd && (
                    <tr style={{ background: '#eff6ff', borderBottom: '2px solid #2563eb' }}>
                      <td style={{ padding: '10px 8px' }}>
                        <select
                          value={inlineServiceRoomType}
                          onChange={(e) => setInlineServiceRoomType(e.target.value)}
                          className="manage-filter-select"
                          style={{ width: '100%', padding: '7px 8px', fontSize: '12px', fontWeight: '600' }}
                        >
                          <option value="ALL">🏢 All Room Types</option>
                          {roomTypes.map((rt) => (
                            <option
                              key={rt.id || rt.name}
                              value={rt.name}
                              disabled={rt.status === 'Inactive'}
                              style={{ color: rt.status === 'Inactive' ? '#dc2626' : 'inherit' }}
                            >
                              {rt.name} {rt.status === 'Inactive' ? '(Inactive)' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <input
                          type="text"
                          placeholder="Service Type (e.g. Nursing Care)"
                          value={inlineServiceType}
                          onChange={(e) => setInlineServiceType(e.target.value)}
                          className="manage-search-input"
                          style={{ minWidth: '100%', padding: '7px 10px', fontSize: '13px' }}
                          autoFocus
                        />
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Amount (₹)"
                          value={inlineServiceUnitCharge}
                          onChange={(e) => setInlineServiceUnitCharge(e.target.value)}
                          className="manage-search-input"
                          style={{ minWidth: '100%', padding: '7px 10px', fontSize: '13px' }}
                        />
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <select
                          value={inlineServiceStatus}
                          onChange={(e) => setInlineServiceStatus(e.target.value)}
                          className="manage-filter-select"
                          style={{ width: '100%', padding: '7px 8px' }}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <select
                          value={inlineServiceChargeEditable ? 'YES' : 'NO'}
                          onChange={(e) => setInlineServiceChargeEditable(e.target.value === 'YES')}
                          className="manage-filter-select"
                          style={{ width: '100%', padding: '7px 8px' }}
                        >
                          <option value="YES">Yes</option>
                          <option value="NO">No</option>
                        </select>
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <button
                            type="button"
                            onClick={handleSaveInlineServiceAdd}
                            style={{
                              background: '#0284c7',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '7px 10px',
                              fontSize: '12px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            ✓ Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowInlineServiceAdd(false)}
                            style={{
                              background: '#ef4444',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '7px 8px',
                              fontSize: '12px',
                              fontWeight: '700',
                              cursor: 'pointer'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {filteredServices.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="ip-manage-empty" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                        No inpatient services found matching your filters. Click &quot;Add Service&quot; to configure a service.
                      </td>
                    </tr>
                  ) : (
                    filteredServices.map((srv) => {
                      const isEditing = editingServiceId === srv.id;
                      const srvRoomType = srv.roomType || 'ALL';
                      const srvName = srv.serviceType || srv.name || '';
                      const srvCharge = srv.unitCharge !== undefined ? srv.unitCharge : srv.price || 0;
                      const srvStatus = srv.status || 'Active';
                      const isEditable = srv.chargeEditable !== false;

                      if (isEditing) {
                        return (
                          <tr key={srv.id} style={{ background: '#fefce8', borderBottom: '2px solid #eab308' }}>
                            <td style={{ padding: '8px' }}>
                              <select
                                value={editServiceRoomType}
                                onChange={(e) => setEditServiceRoomType(e.target.value)}
                                className="manage-filter-select"
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px' }}
                              >
                                <option value="ALL">🏢 All Room Types</option>
                                {roomTypes.map((rt) => (
                                  <option
                                    key={rt.id || rt.name}
                                    value={rt.name}
                                    disabled={rt.status === 'Inactive'}
                                    style={{ color: rt.status === 'Inactive' ? '#dc2626' : 'inherit' }}
                                  >
                                    {rt.name} {rt.status === 'Inactive' ? '(Inactive)' : ''}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="text"
                                value={editServiceType}
                                onChange={(e) => setEditServiceType(e.target.value)}
                                className="manage-search-input"
                                style={{ minWidth: '100%', padding: '6px 10px', fontSize: '13px' }}
                              />
                            </td>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editServiceUnitCharge}
                                onChange={(e) => setEditServiceUnitCharge(e.target.value)}
                                className="manage-search-input"
                                style={{ minWidth: '100%', padding: '6px 10px', fontSize: '13px' }}
                              />
                            </td>
                            <td style={{ padding: '8px' }}>
                              <select
                                value={editServiceStatus}
                                onChange={(e) => setEditServiceStatus(e.target.value)}
                                className="manage-filter-select"
                                style={{ width: '100%', padding: '6px 8px' }}
                              >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                              </select>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <select
                                value={editServiceChargeEditable ? 'YES' : 'NO'}
                                onChange={(e) => setEditServiceChargeEditable(e.target.value === 'YES')}
                                className="manage-filter-select"
                                style={{ width: '100%', padding: '6px 8px' }}
                              >
                                <option value="YES">Yes</option>
                                <option value="NO">No</option>
                              </select>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <button
                                  type="button"
                                  onClick={() => handleSaveInlineServiceEdit(srv.id)}
                                  style={{
                                    background: '#16a34a',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '6px 9px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer'
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingServiceId(null)}
                                  style={{
                                    background: '#ef4444',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '6px 8px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer'
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr key={srv.id} className="ip-manage-row" style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                            <span className="room-type-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 600 }}>
                              <FaBuilding size={12} color="#2563eb" />
                              {srvRoomType === 'ALL' ? 'All Room Types' : srvRoomType}
                            </span>
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>
                                {srvName}
                              </span>
                              {srv.serviceCode && (
                                <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>
                                  {srv.serviceCode}
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '12px 16px', fontWeight: '800', color: '#0f172a', fontSize: '14px' }}>
                            ₹ {Number(srvCharge).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                            <span className={`status-chip ${srvStatus === 'Active' ? 'available' : 'inactive'}`}>
                              • {srvStatus.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '12px 16px' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '3px 9px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 700,
                                background: isEditable ? '#e0f2fe' : '#f1f5f9',
                                color: isEditable ? '#0369a1' : '#64748b',
                                border: isEditable ? '1px solid #bae6fd' : '1px solid #cbd5e1'
                              }}
                            >
                              {isEditable ? '✓ Editable' : '🔒 Fixed'}
                            </span>
                          </td>
                          <td style={{ verticalAlign: 'middle', padding: '12px 16px', textAlign: 'center' }}>
                            <div className="table-actions-group" style={{ justifyContent: 'center' }}>
                              <button
                                className="action-btn-icon edit-btn"
                                onClick={() => {
                                  setEditingServiceId(srv.id);
                                  setEditServiceRoomType(srvRoomType);
                                  setEditServiceType(srvName);
                                  setEditServiceCode(srv.serviceCode || srv.code || '');
                                  setEditServiceUnitCharge(srvCharge);
                                  setEditServiceStatus(srvStatus);
                                  setEditServiceChargeEditable(isEditable);
                                }}
                                title="Edit Service"
                              >
                                <FaEdit size={13} />
                              </button>
                              <button
                                className="action-btn-icon toggle-btn"
                                onClick={() => handleToggleServiceStatus(srv.id)}
                                title="Toggle Active / Inactive"
                              >
                                <FaExchangeAlt size={11} />
                              </button>
                              <button
                                className="action-btn-icon delete-btn"
                                onClick={() => handleDeleteService(srv.id, srvName)}
                                title="Delete Service"
                              >
                                <FaTrashAlt size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Interactive Modals */}
      <HospitalFloorsModal
        isOpen={showHospitalFloorsModal}
        onClose={() => setShowHospitalFloorsModal(false)}
        floors={floors}
        rooms={rooms}
        roomTypes={roomTypes}
        bedTypes={bedTypes}
        onAddFloor={handleAddFloorDirect}
        onDeleteFloor={handleDeleteFloor}
      />

      <RoomTypesModal
        isOpen={showRoomTypesModal}
        onClose={() => setShowRoomTypesModal(false)}
        rooms={rooms}
        onUpdateRoomTypes={setRoomTypes}
      />

      <BedTypesModal
        isOpen={showBedTypesModal}
        onClose={() => setShowBedTypesModal(false)}
        rooms={rooms}
        onUpdateBedTypes={setBedTypes}
      />

      <AddFloorModal
        isOpen={showAddFloorModal}
        onClose={() => setShowAddFloorModal(false)}
        onSaveFloor={handleSaveFloor}
      />

      <AddRoomModal
        isOpen={showAddRoomModal}
        onClose={() => setShowAddRoomModal(false)}
        onSaveRoom={handleSaveRoom}
        floors={floors}
        roomToEdit={roomToEdit}
        configuredRoomTypes={roomTypes}
        configuredBedTypes={bedTypes}
      />

      <AddServiceModal
        isOpen={showAddServiceModal}
        onClose={() => setShowAddServiceModal(false)}
        onSaveService={handleSaveService}
        serviceToEdit={serviceToEdit}
        configuredRoomTypes={roomTypes}
      />

      <ChargeConfigurationModal
        isOpen={showChargeConfigModal}
        onClose={() => setShowChargeConfigModal(false)}
        roomTypeData={selectedConfigRoomType}
        onSave={handleSaveConfigRules}
      />
    </div>
  );
};

export default InpatientManage;
